<template>
  <v-autocomplete
    @input="setDatasource"
    :items="dealerDatasources"
    :value="datasource_id"
    :label="label"
    item-text="name"
    item-value="id"
    :error-messages="errorMessages"
    :disable="disabled"
    no-data-text="No dealer selected -or- dealer has no datasources"
    return-object
  >
    <template v-slot:selection="data">
      <v-chip v-bind="data.attrs" :input-value="data.selected" :color="dsColor(data.item)">
        <v-icon>{{ dsIcon(data.item) }}</v-icon>
        {{ data.item.name }}
      </v-chip>
    </template>
    <template v-slot:item="data">
      <v-chip class="mr-1" :color="dsColor(data.item)">
        <v-icon>{{ dsIcon(data.item) }}</v-icon>
        {{ dsAbbr(data.item) }}
      </v-chip>
      <v-list-item-content>
        <v-list-item-title>{{ data.item.name }}</v-list-item-title>
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

export default {
  name: 'datasource-select',
  props: {
    dealer_id: {
      type: [Number, String],
      required: false,
      default: null
    },
    datasource_id: {
      type: [Number, String],
      required: false,
      default: null
    },
    errorMessages: {
      type: Array
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    onlyCompleteDatasources: {
      type: Boolean,
      required: false,
      default: true
    },
    hideDatasourceReports: {
      type: Boolean,
      required: false,
      default: true
    },
    label: {
      type: String,
      required: false,
      default: 'Datasource Name'
    },
    datasourceType: {
      type: String,
      required: false,
      validator (value) {
        return ['CsvDatasource', 'DmsDatasource', 'SqlDatasource'].includes(value)
      }
    }
  },
  data: function () {
    return {
      datasourceTypeSettings: {
        CsvDatasource: {
          label: 'CSV',
          color: 'cyan lighten-2',
          icon: 'mdi-file-delimited'
        },
        DmsDatasource: {
          label: 'DMS',
          color: 'teal lighten-2',
          icon: 'mdi-database-search'
        },
        SqlDatasource: {
          label: 'SQL',
          color: 'blue lighten-2',
          icon: 'mdi-table-search'
        }
      }
    }
  },
  computed: {
    ...mapState('DatasourceStore', ['datasources', 'datasource']),
    dealerDatasources: function () {
      return this.datasources.filter(ds => ds.dealer_id === this.dealer_id)
    }
  },
  watch: {
    dealer_id: function (newDealerId, oldDealerId) {
      if (newDealerId === oldDealerId) {
        return
      }

      // We only want to execute if the dealer_id changed from some val to some other val
      // don't execute the first time dealer_id is set, i.e. oldDealerId is null
      if (newDealerId && oldDealerId) {
        this.setDatasource({})
      }
    },
    datasource_id: function (val) {
      if (val) {
        this.$store
          .dispatch('DatasourceStore/getDatasource', { id: val })
          .then(ds => this.setDatasource(ds))
          .catch(error => {
            this.$logger.error('Error loading datasource(s) into datasource-select', error.message)
            this.$flash({
              showClose: true,
              message: 'Error Loading Datasource(s). Reload to try again',
              type: 'error'
            })
          })
      } else {
        this.setDatasource({})
      }
    }
  },
  mounted: function () {
    this.fetchDatasources()
  },
  methods: {
    ...mapMutations('DatasourceStore', ['saveDatasource']),
    setDatasource: function (selectedDatasource) {
      this.saveDatasource(selectedDatasource)
      this.$emit('input', selectedDatasource ? selectedDatasource.id : null)
      this.$emit('change', selectedDatasource ? selectedDatasource.id : null)
      this.$emit(
        'update:datasource_id',
        selectedDatasource ? selectedDatasource.id : null
      )
      this.$emit('selected-datasource', selectedDatasource)
    },
    dsColor: function (item) {
      return this.datasourceTypeSettings[item.type].color
    },
    dsAbbr: function (item) {
      return this.datasourceTypeSettings[item.type].label
    },
    dsIcon: function (item) {
      return this.datasourceTypeSettings[item.type].icon
    },
    fetchDatasources: function () {
      const datasourceFilter = {}
      if (this.onlyCompleteDatasources) {
        datasourceFilter.status = 'complete'
      }
      if (this.dealer_id) {
        datasourceFilter.dealer_id = this.dealer_id
      }
      if (this.datasourceType) {
        datasourceFilter.type = this.datasourceType
      }

      if (this.hideDatasourceReports) {
        datasourceFilter.is_report = false
      }

      return this.$store
        .dispatch('DatasourceStore/fetchDatasources', datasourceFilter)
        .catch(error => {
          this.$logger.error('Error Fetching Datasources by dealer')
          this.$flash({
            showClose: true,
            message: `Error fetching datasources by dealer. ${error.message}`,
            type: 'error',
            duration: 0
          })
        })
    }
  }
}
</script>
